<template>
  <v-container fluid>
    <!-- Header con información del supervisor -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="gradient-header">
          <v-card-title class="d-flex align-center">
            <v-icon left color="orange" size="28" icon="mdi-clipboard-list" />
            <span class="text-h5 font-weight-bold">Gestión de Incidencias</span>
            <v-spacer></v-spacer>
            <v-chip
              :color="authStore.user?.role === 'field_supervisor' ? 'green' : 'blue'"
              dark
              label
            >
              <v-icon left size="20" icon="mdi-account-supervisor" />
              Supervisor de Campo
            </v-chip>
          </v-card-title>
          <!-- P13-V7B (refutation): the page reads the incidences of the groups this supervisor
               supervises (`GET /field-supervisor/incidents` filters by group, not by reporter), so it
               no longer says "tus incidencias". -->
          <v-card-subtitle class="text-white">
            Incidencias de los grupos que supervisas
          </v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>

    <!-- Estadísticas rápidas -->
    <v-alert
      v-if="incidentErrorMessage"
      :type="incidentErrorType"
      variant="tonal"
      density="comfortable"
      class="mb-4"
    >
      {{ incidentErrorMessage }}
    </v-alert>

    <!-- P13-V7B (refutation): the server answers ONE page and states no total. When that page is full
         the counts are not published, and the page says so instead of printing a truncated number. -->
    <v-alert
      v-if="statsIncomplete"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-4"
      data-testid="fs-incident-stats-incomplete"
    >
      El servidor devolvió su máximo de {{ statsPageLimit }} incidencias por consulta y no declara el total de la zona: los conteos no se publican.
    </v-alert>

    <!-- P13-V7B (refutation): every card starts as "Sin datos" and returns to it on failure. It used to
         start at 0, fall back to 0 on any error, and label the page length "Mis Incidencias". -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-card class="stats-card">
          <v-card-text class="d-flex align-center">
            <v-avatar color="primary" class="me-3">
              <v-icon color="white" icon="mdi-map-marker-radius" />
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold" data-testid="fs-stat-zone">{{ displayStat(stats.zoneIncidents) }}</div>
              <div class="text-caption text-grey">Incidencias de la zona</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="stats-card">
          <v-card-text class="d-flex align-center">
            <v-avatar color="warning" class="me-3">
              <v-icon color="white" icon="mdi-clock" />
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold" data-testid="fs-stat-open">{{ displayStat(stats.openIncidents) }}</div>
              <div class="text-caption text-grey">Abiertas</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="stats-card">
          <v-card-text class="d-flex align-center">
            <v-avatar color="success" class="me-3">
              <v-icon color="white" icon="mdi-check-circle" />
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold" data-testid="fs-stat-closed">{{ displayStat(stats.closedIncidents) }}</div>
              <div class="text-caption text-grey">Cerradas</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="stats-card">
          <v-card-text class="d-flex align-center">
            <v-avatar color="info" class="me-3">
              <v-icon color="white" icon="mdi-account-group" />
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold" data-testid="fs-stat-others">{{ displayStat(stats.reportedByOthers) }}</div>
              <div class="text-caption text-grey">Reportadas por otros</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Navegación por pestañas -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-tabs v-model="activeTab" show-arrows>
            <!-- P13-V7B (refutation): the history lists the zone's incidences, not the supervisor's own. -->
            <v-tab value="history">
              <v-icon left icon="mdi-history" />
              Historial de la Zona
            </v-tab>
            <v-tab value="active">
              <v-icon left icon="mdi-clock-outline" />
              Incidencias Activas
            </v-tab>
            <v-tab value="employee">
              <v-icon left icon="mdi-account-group-outline" />
              Reportes del Equipo
            </v-tab>
            <v-tab value="create">
              <v-icon left icon="mdi-plus" />
              Nueva Incidencia
            </v-tab>
          </v-tabs>

          <v-window v-model="activeTab">
            <!-- Historial de mis incidencias -->
            <v-window-item value="history">
              <MyIncidentsHistory 
                :supervisor-id="authStore.user?.id || 0"
                @refresh-stats="loadStats"
              />
            </v-window-item>

            <!-- Incidencias activas -->
            <v-window-item value="active">
              <ActiveIncidentsStatus 
                :supervisor-id="authStore.user?.id || 0"
                @refresh-stats="loadStats"
              />
            </v-window-item>

            <!-- Incidencias reportadas por empleados -->
            <v-window-item value="employee">
              <EmployeeReportedIncidents 
                :supervisor-id="authStore.user?.id || 0"
                @refresh-stats="loadStats"
              />
            </v-window-item>

            <!-- Crear nueva incidencia -->
            <v-window-item value="create">
              <CreateIncidentForm 
                v-if="supervisorId"
                :supervisor-id="supervisorId"
                @incident-created="onIncidentCreated"
              />
              <v-alert
                v-else
                type="warning"
                variant="tonal"
                class="ma-4"
              >
                No hay usuario autenticado para crear incidencias con alcance de supervisor.
              </v-alert>
            </v-window-item>
          </v-window>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="showNotification"
      :color="notificationColor"
      :timeout="4000"
      top
    >
      {{ notificationMessage }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="showNotification = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import SupervisorService, { FS_ZONE_INCIDENTS_PAGE_LIMIT } from '@/services/supervisorService'
import MyIncidentsHistory from '@/components/supervisor/incidents/MyIncidentsHistory.vue'
import ActiveIncidentsStatus from '@/components/supervisor/incidents/ActiveIncidentsStatus.vue'
import EmployeeReportedIncidents from '@/components/supervisor/incidents/EmployeeReportedIncidents.vue'
import CreateIncidentForm from '@/components/supervisor/incidents/CreateIncidentForm.vue'

// ================== ESTADO REACTIVO ==================
const authStore = useAuthStore()
const activeTab = ref('history')
const loading = ref(false)
const showNotification = ref(false)
const notificationMessage = ref('')
const notificationColor = ref('success')
const incidentErrorMessage = ref('')
const incidentErrorType = ref<'error' | 'warning' | 'info'>('error')

// Estadísticas
// P13-V7B (refutation): no count is known before the server answers, so none is shown — `null` renders
// "Sin datos". The same state is restored on failure; a failed read is not a measured zero.
const UNKNOWN_STATS = Object.freeze({
  zoneIncidents: null as number | null,
  openIncidents: null as number | null,
  closedIncidents: null as number | null,
  reportedByOthers: null as number | null
})
const stats = ref({ ...UNKNOWN_STATS })
/** P13-V7B (refutation): true when the server returned a full page, i.e. did not state the zone total. */
const statsIncomplete = ref(false)
const statsPageLimit = ref(FS_ZONE_INCIDENTS_PAGE_LIMIT)
const displayStat = (value: number | null) => (typeof value === 'number' ? value : 'Sin datos')

// ================== COMPUTED ==================
const supervisorId = computed(() => {
  const userId = authStore.user?.id || null
  console.log('🧮 SupervisorIncidentsView: Computed supervisorId:', userId)
  console.log('🧮 SupervisorIncidentsView: authStore.user:', authStore.user)
  return userId
})

const formatSupervisorError = (error: any) => {
  const status = error?.response?.status || error?.status
  const code = error?.response?.data?.code || error?.code

  if (status === 403 || code === 'FIELD_SUPERVISOR_SCOPE_DENIED') {
    incidentErrorType.value = 'error'
    return error?.response?.data?.message || 'Acceso denegado para el alcance del supervisor de campo.'
  }

  if (status === 501 || code === 'NOT_IMPLEMENTED_YET') {
    incidentErrorType.value = 'info'
    return error?.response?.data?.message || 'Funcionalidad pendiente de implementación backend con alcance real.'
  }

  incidentErrorType.value = 'warning'
  return error?.response?.data?.message || error?.message || 'No fue posible cargar datos reales de incidencias.'
}

// ================== MÉTODOS ==================
/**
 * Carga las estadísticas del supervisor
 */
const loadStats = async () => {
  if (!supervisorId.value) {
    console.warn('⚠️ No hay supervisorId disponible')
    incidentErrorType.value = 'warning'
    incidentErrorMessage.value = 'No hay usuario autenticado para cargar incidencias del supervisor.'
    return
  }
  
  try {
    loading.value = true
    console.log('🔄 SupervisorIncidentsView: Cargando estadísticas para supervisor:', supervisorId.value)
    
    const response = await SupervisorService.getIncidentStats(supervisorId.value)
    incidentErrorMessage.value = ''

    // P13-V7B (refutation): the counts are the service's, and they are `null` whenever the server did
    // not state the whole zone (a full page). They are never replaced by a local figure.
    stats.value = {
      zoneIncidents: response.zoneIncidents,
      openIncidents: response.openIncidents,
      closedIncidents: response.closedIncidents,
      reportedByOthers: response.reportedByOthers
    }
    statsIncomplete.value = !response.complete
    statsPageLimit.value = response.pageLimit

  } catch (error) {
    console.error('❌ SupervisorIncidentsView: Error loading stats:', error)
    incidentErrorMessage.value = formatSupervisorError(error)
    showNotification.value = true
    notificationMessage.value = incidentErrorMessage.value
    notificationColor.value = 'error'

    // P13-V7B (refutation): a failed read publishes NOTHING — "Sin datos", not zeros.
    stats.value = { ...UNKNOWN_STATS }
    statsIncomplete.value = false

  } finally {
    loading.value = false
  }
}

/**
 * Maneja la creación exitosa de una incidencia
 */
const onIncidentCreated = () => {
  showNotification.value = true
  notificationMessage.value = 'Incidencia creada exitosamente'
  notificationColor.value = 'success'
  activeTab.value = 'history'
  loadStats()
}

/**
 * Muestra una notificación
 */
const showMessage = (message: string, color: string = 'success') => {
  notificationMessage.value = message
  notificationColor.value = color
  showNotification.value = true
}

// ================== CICLO DE VIDA ==================
onMounted(async () => {
  console.log('🚀 SupervisorIncidentsView: Componente montado')
  // P13-V7B (refutation): the mount no longer seeds the cards with zeros; they stay "Sin datos" until
  // the server answers.
  await loadStats()
})

// ================== EXPOSICIÓN ==================
defineExpose({
  loadStats,
  showMessage
})
</script>

<style scoped>
.gradient-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.stats-card {
  transition: transform 0.2s ease-in-out;
}

.stats-card:hover {
  transform: translateY(-2px);
}

.v-tab {
  font-weight: 500;
}

.v-window {
  min-height: 400px;
}

.text-h6 {
  line-height: 1.2;
}

.text-caption {
  opacity: 0.7;
}
</style>
